import { useState } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import FeatureRenameDialog from "@/v2-backoffice-client/feature/rename/FeatureRenameDialog"
import { useLayerQuery } from "@/v2-backoffice-client/layer/layer.query"
import LayerStatusFilterChips from "@/v2-backoffice-client/layer/counts/LayerStatusFilterChips"
import LayerCategoryLayerAddManySelector from "@/v2-backoffice-client/layer/category/layer/add/many/LayerCategoryLayerAddManySelector"
import LayerActionMenu from "@/v2-backoffice-client/layer/LayerActionMenu"
import LayerProdStateCell from "@/v2-backoffice-client/layer/authoring/LayerProdStateCell"
import LayerUptimeCell from "@/v2-backoffice-client/layer/LayerUptimeCell"
import { LayerUptimeMapProvider } from "@/v2-backoffice-client/layer/LayerUptimeMapContext"
import LayerDriftCell from "@/v2-backoffice-client/layer/drift/LayerDriftCell"
import { LayerDriftMapProvider } from "@/v2-backoffice-client/layer/drift/LayerDriftMapContext"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import { AGOL_SEARCH_BASE_URL } from "@/v2-backoffice-client/layer/layer.constants"
import {
  LAYER_SEARCH_COLUMN,
  LAYER_SEARCH_COLUMN_ID,
  LAYER_SEARCH_PLACEHOLDER,
} from "@/v2-backoffice-client/layer/layer.columns"
import { IS_CATALOG_AUTHORING_ENABLED } from "@/v2-backoffice-client/feature/feature.constants"

const COLUMN_SIZING = {
  Priority: 60,
  Has3dSupport: 100,
  IsEnabled: 60,
  ProdState: 120,
  Uptime: 220,
  Drift: 120,
  CreatedAt: 130,
  UpdatedAt: 130,
}

const columns = createColumns<LayerFragment>({
  enableSelection: true,
  enableSorting: true,
  enableHiding: true,
  columns: {
    Priority: { id: "Priority", label: "Order" },
    Name: {
      id: "Name",
      label: "Name",
      to: ({ row }) => `?v=layer:${row.original.FeatureCode}`,
    },
    Description: {
      id: "Description",
      label: "Description",
      defaultHidden: true,
      renderCell({ row }) {
        const desc = row.original.Description
        if (!desc) return null
        return (
          <span className="truncate max-w-[200px]" title={desc}>
            {desc}
          </span>
        )
      },
    },
    FeatureCode: {
      id: "FeatureCode",
      label: "Feature Code",
      filter: { type: "text", placeholder: "Filter by code..." },
      to: ({ value }) => `${AGOL_SEARCH_BASE_URL}${value}`,
    },
    [LAYER_SEARCH_COLUMN_ID]: LAYER_SEARCH_COLUMN,
    LayerTypeCode: {
      id: "LayerTypeCode",
      label: "Type",
      enableGrouping: true,
    },
    Has3dSupport: {
      id: "Has3dSupport",
      label: "3D Support",
      enableGrouping: true,
      renderCell({ value }) {
        return (
          <DotText variant={value ? "success-1" : "destructive"}>
            {value ? "3D supported" : "Only 2D"}
          </DotText>
        )
      },
      exportValue({ value }) {
        return value ? "3D supported" : "Only 2D"
      },
    },
    IsEnabled: {
      id: "IsEnabled",
      label: "Status",
      renderCell({ value }) {
        return (
          <DotText variant={value ? "success-1" : "destructive"}>
            {value ? "Enabled" : "Disabled"}
          </DotText>
        )
      },
      exportValue({ value }) {
        return value ? "Enabled" : "Disabled"
      },
    },
    ProdState: {
      id: "ProdState",
      label: "Production",
      description:
        "Layers are edited here, in the dev catalog, and copied to the production catalog with Push prod. This says whether production has the layer's current definition.",
      // Joined client-side from the shared authoring-statuses query; no
      // accessor on the layer fragment (see LayerProdStateCell). Hidden on
      // prod deployments, where no authoring catalog exists.
      enableSorting: false,
      defaultHidden: !IS_CATALOG_AUTHORING_ENABLED,
      renderCell({ row }) {
        return <LayerProdStateCell featureCode={row.original.FeatureCode} />
      },
    },
    Uptime: {
      id: "Uptime",
      label: "Uptime",
      // No accessor on the layer fragment for this -- the value is
      // joined client-side via the LayerUptimeMapContext provider
      // in this same component. Sorting/filtering on it would
      // require pulling the joined data into the row, which we
      // explicitly avoid to keep the layer query slim.
      enableSorting: false,
      renderCell({ row }) {
        return <LayerUptimeCell featureCode={row.original.FeatureCode} />
      },
    },
    Drift: {
      id: "Drift",
      label: "Drift",
      // Joined client-side via LayerDriftMapContext, same trade-off as
      // the Uptime column above.
      enableSorting: false,
      renderCell({ row }) {
        return <LayerDriftCell featureCode={row.original.FeatureCode} />
      },
    },
    CreatedAt: {
      id: "CreatedAt",
      label: "Created",
      defaultHidden: true,
      renderCell({ row }) {
        if (!row.original.CreatedAt) return null
        return formatDateString(String(row.original.CreatedAt))
      },
      exportValue({ row }) {
        if (!row.original.CreatedAt) return null
        return formatDateString(String(row.original.CreatedAt))
      },
    },
    UpdatedAt: {
      id: "UpdatedAt",
      label: "Updated",
      defaultHidden: true,
      renderCell({ row }) {
        if (!row.original.UpdatedAt) return null
        return formatDateString(String(row.original.UpdatedAt))
      },
      exportValue({ row }) {
        if (!row.original.UpdatedAt) return null
        return formatDateString(String(row.original.UpdatedAt))
      },
    },
    ReviewedAt: {
      id: "ReviewedAt",
      label: "Reviewed",
      defaultHidden: true,
      renderCell({ row }) {
        if (!row.original.ReviewedAt) return null
        return formatDateString(String(row.original.ReviewedAt))
      },
      exportValue({ row }) {
        if (!row.original.ReviewedAt) return null
        return formatDateString(String(row.original.ReviewedAt))
      },
    },
    ReviewedByName: {
      id: "ReviewedByName",
      label: "Reviewed by",
      defaultHidden: true,
    },
    ReviewNote: {
      id: "ReviewNote",
      label: "Review note",
      defaultHidden: true,
      renderCell({ row }) {
        const note = row.original.ReviewNote
        if (!note) return null
        return (
          <span className="truncate max-w-[200px]" title={note}>
            {note}
          </span>
        )
      },
    },
  },
})

export type LayerTableProps = Omit<
  TableProps<LayerFragment>,
  "data" | "columns"
>

function LayerTable(props: LayerTableProps) {
  const { className, ...tableProps } = props

  const opener = useAppViewSheetOpener()
  const [isEnabled, setIsEnabled] = useState<boolean | undefined>(true)

  const { data, previousData, loading, error, refetch } = useLayerQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      input: isEnabled === undefined ? undefined : { isEnabled },
    },
  })

  const displayData = data ?? previousData
  const layers = displayData?.getLayers ?? []

  // The rename dialog lives up here because the row toolbar that opens it
  // only exists while its row is hovered — a dialog mounted inside it would
  // unmount the moment the pointer leaves for the dialog.
  const [renameTarget, setRenameTarget] = useState<LayerFragment | null>(null)

  return (
    <LayerUptimeMapProvider>
      <LayerDriftMapProvider>
        <Table
          className={cn("layer-table", className)}
          tableId="layer-table"
          data={layers}
          columns={columns}
          initialColumnSizing={COLUMN_SIZING}
          enableGrouping
          enableRowSelection
          getHighlightedRowId={(row) => {
            return opener.getIsActive("layer", String(row.original.FeatureCode))
          }}
          {...tableProps}
        >
          <Table.Toolbar>
            <LayerStatusFilterChips
              isEnabled={isEnabled}
              onChange={setIsEnabled}
            />

            <div className="flex-1" />

            <Table.Search
              column={LAYER_SEARCH_COLUMN_ID}
              placeholder={LAYER_SEARCH_PLACEHOLDER}
            />
            <Separator orientation="vertical" />
            <Table.ToolbarMenu onRefresh={refetch} exportFileName="layers" />
          </Table.Toolbar>

          <Table.FilterList />
          <Table.GroupList />

          <Table.RowToolbar<LayerFragment> column="Name">
            {(layer) => (
              <LayerActionMenu
                layer={layer}
                onRename={() => setRenameTarget(layer)}
              />
            )}
          </Table.RowToolbar>

          <Table.Content isLoading={loading} error={error} />

          <Table.SelectionToolbar<LayerFragment>>
            {(layers, _reset) => {
              const codes = layers.map((l) => l.FeatureCode)
              return (
                <>
                  <Table.ExportSelectionButton
                    fileName="layers"
                    selectionCount={layers.length}
                  />
                  <LayerCategoryLayerAddManySelector
                    variant="ghost"
                    isStacked
                    layerFeatureCodes={codes}
                  >
                    Category
                  </LayerCategoryLayerAddManySelector>
                </>
              )
            }}
          </Table.SelectionToolbar>
        </Table>

        {renameTarget ? (
          <FeatureRenameDialog
            featureCode={renameTarget.FeatureCode}
            name={renameTarget.Name}
            open
            onOpenChange={(open) => {
              if (!open) setRenameTarget(null)
            }}
          />
        ) : null}
      </LayerDriftMapProvider>
    </LayerUptimeMapProvider>
  )
}

export default LayerTable
